import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { authService } from '../services/auth';
import { APIError } from '../types';

const Login: React.FC = () => {
  const [formData, setFormData] = useState({
    login_identifier: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await authAPI.login(formData.login_identifier, formData.password);
      authService.setToken(response.token);
      authService.setUser(response.user);

      // Redirect based on role — must mirror PublicRoute in App.tsx
      if (response.user.role === 'superadmin') {
        navigate('/admin/dashboard');
      } else if (response.user.role === 'faculty') {
        navigate('/admin/documents');
      } else {
        navigate('/chat');
      }
    } catch (err: any) {
      const apiError = err.response?.data as APIError;
      setError(apiError?.error || err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-8 px-4 sm:py-12 sm:px-6 lg:px-8 hero-section relative z-10">
      <div className="max-w-md w-full animate-slide-up">
        {/* Logo and Header */}
        <div className="text-center mb-7 sm:mb-9">
          <div className="inline-flex items-center justify-center mb-4 sm:mb-5">
            <img
              src="https://edifywebsite.blob.core.windows.net/edify-files/IMG_9812.JPG"
              alt="BM Law College Logo"
              className="w-28 h-28 sm:w-36 sm:h-36 object-contain rounded-2xl shadow-soft-xl ring-1 ring-bmlc-gold-500/30"
            />
          </div>
          <h1 className="text-3xl sm:text-4xl font-serif font-bold tracking-tight text-white mb-2">
            BM Law College
          </h1>
          <p className="text-bmlc-gold-400 text-base sm:text-lg font-medium">
            AI-Powered Legal Research Assistant
          </p>
          <p className="text-gray-300 text-xs sm:text-sm mt-2 tracking-wide">
            Advanced learning platform for legal excellence
          </p>
        </div>

        {/* Login Card */}
        <div className="glass rounded-2xl overflow-hidden shadow-soft-xl">
          <div className="h-[3px] bg-gold-hairline" />
          <div className="p-6 sm:p-8">
            <div className="mb-6 sm:mb-7">
              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-bmlc-navy-900 mb-1.5">Welcome Back</h2>
              <p className="text-gray-600 text-xs sm:text-sm">Sign in to access your legal AI assistant</p>
            </div>

            <form className="space-y-4 sm:space-y-5" onSubmit={handleSubmit}>
              {error && (
                <div className="toast-error text-xs sm:text-sm animate-fade-in">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p>{error}</p>
                </div>
              )}

              <div>
                <label htmlFor="login_identifier" className="block text-xs sm:text-sm font-semibold text-bmlc-navy-900 mb-2 tracking-wide">
                  Email or Phone Number
                </label>
                <input
                  id="login_identifier"
                  name="login_identifier"
                  type="text"
                  required
                  className="input-premium py-2.5 sm:py-3 text-sm"
                  placeholder="Enter email or phone number"
                  value={formData.login_identifier}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-xs sm:text-sm font-semibold text-bmlc-navy-900 mb-2 tracking-wide">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    className="input-premium py-2.5 sm:py-3 text-sm pr-11 sm:pr-12"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                    title={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-bmlc-navy-700 focus:outline-none focus-visible:text-bmlc-navy-700 transition-colors duration-200"
                  >
                    {showPassword ? (
                      /* eye-off */
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.542 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      /* eye */
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full py-2.5 sm:py-3 px-4 rounded-xl text-white font-semibold text-xs sm:text-sm flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 sm:h-5 sm:w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <span>Sign In</span>
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 sm:mt-8 text-center">
          <div className="divider-gold my-0 mb-3 max-w-[12rem] mx-auto opacity-70" />
          <p className="text-gray-400 text-xs sm:text-sm tracking-wide">
            © 2024 BM Law College. Empowering Legal Education
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
